/**
 * AIRA Voice Orb - Phase 3.2
 * 
 * Visual orb component that reflects AIRA's voice state:
 * - IDLE: Soft breathing animation
 * - LISTENING: Active pulsing, microphone wave
 * - UNDERSTANDING: Processing wave
 * - THINKING: Neural network animation
 * - SEARCHING/PLANNING/EXECUTING: State-specific visuals
 * - SPEAKING: Audio wave ripples
 * - SUCCESS: Green confirmation pulse
 * - INTERRUPTED: Quick fade
 * - ERROR: Red pulse
 * 
 * Connects to the existing AIVoiceVisualizer for consistent styling
 */

import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, MicOff, Volume2, VolumeX, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { VOICE_STATES } from '../hooks/useVoiceClient';

// State colors
const STATE_COLORS = {
  [VOICE_STATES.IDLE]: {
    primary: '#6366f1',
    secondary: '#8b5cf6',
    glow: 'rgba(99, 102, 241, 0.3)',
  },
  [VOICE_STATES.INITIALIZING]: {
    primary: '#6366f1',
    secondary: '#8b5cf6',
    glow: 'rgba(99, 102, 241, 0.3)',
  },
  [VOICE_STATES.CONNECTED]: {
    primary: '#10b981',
    secondary: '#34d399',
    glow: 'rgba(16, 185, 129, 0.3)',
  },
  [VOICE_STATES.LISTENING]: {
    primary: '#ff2d92',
    secondary: '#b347d9',
    glow: 'rgba(255, 45, 146, 0.4)',
  },
  [VOICE_STATES.TRANSCRIBING]: {
    primary: '#ff2d92',
    secondary: '#ff6b35',
    glow: 'rgba(255, 45, 146, 0.4)',
  },
  [VOICE_STATES.UNDERSTANDING]: {
    primary: '#f59e0b',
    secondary: '#fbbf24',
    glow: 'rgba(245, 158, 11, 0.4)',
  },
  [VOICE_STATES.THINKING]: {
    primary: '#8b5cf6',
    secondary: '#a78bfa',
    glow: 'rgba(139, 92, 246, 0.4)',
  },
  [VOICE_STATES.SEARCHING]: {
    primary: '#06b6d4',
    secondary: '#22d3ee',
    glow: 'rgba(6, 182, 212, 0.4)',
  },
  [VOICE_STATES.PLANNING]: {
    primary: '#8b5cf6',
    secondary: '#c4b5fd',
    glow: 'rgba(139, 92, 246, 0.4)',
  },
  [VOICE_STATES.EXECUTING]: {
    primary: '#f97316',
    secondary: '#fb923c',
    glow: 'rgba(249, 115, 22, 0.4)',
  },
  [VOICE_STATES.SPEAKING]: {
    primary: '#00d4ff',
    secondary: '#00f0ff',
    glow: 'rgba(0, 212, 255, 0.4)',
  },
  [VOICE_STATES.SUCCESS]: {
    primary: '#10b981',
    secondary: '#34d399',
    glow: 'rgba(16, 185, 129, 0.5)',
  },
  [VOICE_STATES.INTERRUPTED]: {
    primary: '#f59e0b',
    secondary: '#fbbf24',
    glow: 'rgba(245, 158, 11, 0.3)',
  },
  [VOICE_STATES.ERROR]: {
    primary: '#ef4444',
    secondary: '#f87171',
    glow: 'rgba(239, 68, 68, 0.5)',
  },
  [VOICE_STATES.DISCONNECTED]: {
    primary: '#6b7280',
    secondary: '#9ca3af',
    glow: 'rgba(107, 114, 128, 0.2)',
  },
};

// State labels
const STATE_LABELS = {
  [VOICE_STATES.IDLE]: 'Tap to speak',
  [VOICE_STATES.INITIALIZING]: 'Connecting...',
  [VOICE_STATES.CONNECTED]: 'Ready',
  [VOICE_STATES.LISTENING]: 'Listening...',
  [VOICE_STATES.TRANSCRIBING]: 'Processing...',
  [VOICE_STATES.UNDERSTANDING]: 'Understanding...',
  [VOICE_STATES.THINKING]: 'Thinking...',
  [VOICE_STATES.SEARCHING]: 'Searching...',
  [VOICE_STATES.PLANNING]: 'Planning...',
  [VOICE_STATES.EXECUTING]: 'Executing...',
  [VOICE_STATES.SPEAKING]: 'Speaking...',
  [VOICE_STATES.SUCCESS]: 'Done',
  [VOICE_STATES.INTERRUPTED]: 'Interrupted',
  [VOICE_STATES.ERROR]: 'Error',
  [VOICE_STATES.DISCONNECTED]: 'Disconnected',
};

// Animation variants based on state
const getOrbVariants = (state) => {
  const isActive = [
    VOICE_STATES.LISTENING,
    VOICE_STATES.TRANSCRIBING,
    VOICE_STATES.UNDERSTANDING,
    VOICE_STATES.THINKING,
    VOICE_STATES.SEARCHING,
    VOICE_STATES.PLANNING,
    VOICE_STATES.EXECUTING,
    VOICE_STATES.SPEAKING,
  ].includes(state);

  const isError = state === VOICE_STATES.ERROR;
  const isSuccess = state === VOICE_STATES.SUCCESS;

  return {
    initial: { scale: 1, opacity: 0.8 },
    animate: {
      scale: isActive ? [1, 1.1, 1] : isError ? [1, 1.05, 1] : [1, 1.03, 1],
      opacity: isActive ? [0.8, 1, 0.8] : 0.9,
      transition: {
        duration: isActive ? 0.8 : isError ? 0.3 : 2,
        repeat: Infinity,
        ease: 'easeInOut',
      },
    },
  };
};

/**
 * Voice Orb Component
 */
export function AIRAVoiceOrb({
  state = VOICE_STATES.IDLE,
  size = 'lg',
  onClick,
  onLongPress,
  disabled = false,
  showLabel = true,
  transcript = '',
  className = '',
}) {
  // Size configurations
  const sizes = {
    sm: { orb: 80, icon: 24, ring: 4 },
    md: { orb: 120, icon: 32, ring: 5 },
    lg: { orb: 160, icon: 40, ring: 6 },
    xl: { orb: 200, icon: 48, ring: 7 },
  };

  const config = sizes[size] || sizes.lg;
  const colors = STATE_COLORS[state] || STATE_COLORS[VOICE_STATES.IDLE];
  const label = STATE_LABELS[state] || 'Unknown';
  const variants = useMemo(() => getOrbVariants(state), [state]);

  // Determine icon
  const Icon = useMemo(() => {
    switch (state) {
      case VOICE_STATES.LISTENING:
      case VOICE_STATES.TRANSCRIBING:
        return Mic;
      case VOICE_STATES.SPEAKING:
        return Volume2;
      case VOICE_STATES.ERROR:
        return AlertCircle;
      case VOICE_STATES.SUCCESS:
        return CheckCircle;
      case VOICE_STATES.INITIALIZING:
      case VOICE_STATES.UNDERSTANDING:
      case VOICE_STATES.THINKING:
      case VOICE_STATES.SEARCHING:
      case VOICE_STATES.PLANNING:
      case VOICE_STATES.EXECUTING:
        return Loader2;
      case VOICE_STATES.DISCONNECTED:
        return MicOff;
      default:
        return Mic;
    }
  }, [state]);

  const isSpinning = [
    VOICE_STATES.INITIALIZING,
    VOICE_STATES.UNDERSTANDING,
    VOICE_STATES.THINKING,
    VOICE_STATES.SEARCHING,
    VOICE_STATES.PLANNING,
    VOICE_STATES.EXECUTING,
  ].includes(state);

  const isActive = [
    VOICE_STATES.LISTENING,
    VOICE_STATES.TRANSCRIBING,
  ].includes(state);

  return (
    <div className={`relative flex flex-col items-center ${className}`}>
      {/* Outer glow rings */}
      <AnimatePresence>
        {isActive && (
          <>
            {[1, 2, 3].map((ring) => (
              <motion.div
                key={ring}
                className="absolute rounded-full"
                style={{
                  width: config.orb + ring * 40,
                  height: config.orb + ring * 40,
                  border: `2px solid ${colors.primary}`,
                  opacity: 0.3 - ring * 0.08,
                }}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3 - ring * 0.08, 0, 0.3 - ring * 0.08],
                }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: ring * 0.2,
                  ease: 'easeOut',
                }}
              />
            ))}
          </>
        )}
      </AnimatePresence>

      {/* Main orb button */}
      <motion.button
        className="relative rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-black"
        style={{
          width: config.orb,
          height: config.orb,
          background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
          boxShadow: `0 0 ${isActive ? 40 : 20}px ${colors.glow}, inset 0 0 20px rgba(255,255,255,0.1)`,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
        variants={variants}
        initial="initial"
        animate="animate"
        whileHover={disabled ? {} : { scale: 1.05 }}
        whileTap={disabled ? {} : { scale: 0.95 }}
        onClick={onClick}
        disabled={disabled}
        data-testid="aira-voice-orb"
      >
        {/* Inner glow */}
        <div
          className="absolute inset-2 rounded-full"
          style={{
            background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.3), transparent 70%)`,
          }}
        />

        {/* Icon */}
        <div className="absolute inset-0 flex items-center justify-center">
          <Icon
            className={`text-white ${isSpinning ? 'animate-spin' : ''}`}
            style={{ width: config.icon, height: config.icon }}
          />
        </div>

        {/* Audio wave bars (for listening/speaking) */}
        {(state === VOICE_STATES.LISTENING || state === VOICE_STATES.SPEAKING) && (
          <div className="absolute inset-0 flex items-center justify-center gap-1 pointer-events-none">
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                className="bg-white/60 rounded-full"
                style={{ width: 3 }}
                animate={{
                  height: [8, 24, 8],
                }}
                transition={{
                  duration: 0.5,
                  repeat: Infinity,
                  delay: i * 0.1,
                  ease: 'easeInOut',
                }}
              />
            ))}
          </div>
        )}
      </motion.button>

      {/* State label */}
      {showLabel && (
        <motion.div
          className="mt-4 text-center"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          key={state}
        >
          <div className="flex items-center justify-center gap-2">
            {isActive && (
              <motion.span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: colors.primary }}
                animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
                transition={{ duration: 0.8, repeat: Infinity }}
              />
            )}
            <span
              className="text-sm font-medium"
              style={{ color: colors.primary }}
            >
              {label}
            </span>
          </div>

          {/* Transcript display */}
          {transcript && (
            <motion.p
              className="mt-2 text-gray-400 text-sm max-w-xs truncate"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              "{transcript}"
            </motion.p>
          )}
        </motion.div>
      )}
    </div>
  );
}

/**
 * Compact Voice Button
 * Smaller version for embedding in chat interfaces
 */
export function AIRAVoiceButton({
  state = VOICE_STATES.IDLE,
  onClick,
  disabled = false,
  size = 48,
  className = '',
}) {
  const colors = STATE_COLORS[state] || STATE_COLORS[VOICE_STATES.IDLE];
  const isActive = [VOICE_STATES.LISTENING, VOICE_STATES.SPEAKING].includes(state);
  const isSpinning = [VOICE_STATES.THINKING, VOICE_STATES.UNDERSTANDING].includes(state);

  const Icon = state === VOICE_STATES.LISTENING ? Mic : 
               state === VOICE_STATES.SPEAKING ? Volume2 :
               isSpinning ? Loader2 : Mic;

  return (
    <motion.button
      className={`relative rounded-full flex items-center justify-center focus:outline-none ${className}`}
      style={{
        width: size,
        height: size,
        background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
        boxShadow: `0 0 ${isActive ? 20 : 10}px ${colors.glow}`,
      }}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      onClick={onClick}
      disabled={disabled}
      data-testid="aira-voice-button"
    >
      <Icon
        className={`text-white ${isSpinning ? 'animate-spin' : ''}`}
        style={{ width: size * 0.5, height: size * 0.5 }}
      />
      
      {isActive && (
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{ border: `2px solid ${colors.primary}` }}
          animate={{ scale: [1, 1.3, 1], opacity: [0.8, 0, 0.8] }}
          transition={{ duration: 1, repeat: Infinity }}
        />
      )}
    </motion.button>
  );
}

export default AIRAVoiceOrb;
